import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { useSession } from '@/services/providers/use-session.ts';
import {
  getAssignableRoles,
  InviteUserFormData,
} from '@/features/users/schemas/invite-user.schema.ts';

export function CreateInviteUserIdentityForm() {
  const { t } = useTranslation();
  const { role: inviterRole } = useSession();
  const form = useFormContext<InviteUserFormData>();

  const assignableRoles = useMemo(() => getAssignableRoles(inviterRole), [inviterRole]);

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-semibold">{t('users.invite.steps.identity.title')}</h2>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="required">
                {t('users.invite.fields.firstName.label')}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('users.invite.fields.firstName.placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="required">
                {t('users.invite.fields.lastName.label')}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('users.invite.fields.lastName.placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="required">{t('users.invite.fields.email.label')}</FormLabel>
            <FormControl>
              <Input
                type="email"
                placeholder={t('users.invite.fields.email.placeholder')}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="role"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="required">{t('users.invite.fields.role.label')}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('users.invite.fields.role.placeholder')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {assignableRoles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {t(`users.invite.roles.${role}`, { defaultValue: role })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
